import { untrack } from "svelte";
import { debounce } from "../debounce";

export interface DelayedVisualLoading {
	show: boolean;
}

export interface DelayedVisualLoadingSettings {
	/** Whether the process is currently loading. */
	loading: boolean;

	/** How many milliseconds to wait. */
	ms: number;

	/**
	 * Whether the first time `loading` is true the visual delay should happen.
	 * @default true
	 */
	delayFirst?: boolean;
}

function defaultedDelayedVisualLoadingSettings(
	settings: DelayedVisualLoadingSettings,
): Required<DelayedVisualLoadingSettings> {
	return {
		delayFirst: true,
		...settings,
	};
}

/**
 * Helper for displaying loading state only after a certain delay to prevent flashes.
 *
 * @example
 * const promise = createTrackedPromise(() => sleep(2000));
 * const delayedLoading = delayedVisualLoading(() => ({
 *   loading: promise.tracked.status === "pending",
 *   ms: 500,
 * }));
 *
 */
export function delayedVisualLoading(
	// This parameter is a function so Svelte can reactively track changes inside it without using $derived which would recall this whole function and lose local state.
	settings: () => DelayedVisualLoadingSettings,
): DelayedVisualLoading {
	let show = $state(false);
	let first = $state(true);
	const { ms, loading, delayFirst } = $derived(defaultedDelayedVisualLoadingSettings(settings()));

	const debounced = $derived(
		debounce((signal) => {
			if (signal.aborted) {
				return;
			}
			show = true;
		}, ms),
	);

	$effect(() => {
		const { call, abortAll } = debounced;
		show = false;
		if (loading) {
			if (delayFirst || !untrack(() => first)) {
				void call();
			} else {
				show = true;
			}
			first = false;
		} else {
			abortAll();
		}
		return abortAll;
	});

	return {
		get show(): boolean {
			return show;
		},
	};
}
